/**
 * Integrations Section Component
 *
 * Settings page section displaying available integration providers.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plug } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { IntegrationCard } from "./IntegrationCard";
import { LogfireConfigDialog } from "./LogfireConfigDialog";
import { TeamsConfigDialog } from "./TeamsConfigDialog";
import { CloudWatchConfigDialog } from "./CloudWatchConfigDialog";
import { JiraConfigDialog } from "./JiraConfigDialog";
import { SlackConfigDialog } from "./SlackConfigDialog";
import { AwsDiscoveryConfigDialog } from "./AwsDiscoveryConfigDialog";
import { VertexConfigDialog } from "./VertexConfigDialog";
import { FeatureGate } from "@/components/auth/FeatureGate";
import { useLogfireTokens } from "@/hooks/useLogfireTokens";
import { useOrganizationContext } from "@/providers/OrganizationProvider";
import { useBedrockConnections } from "@/hooks/useBedrock";
import { useSageMakerConnections } from "@/hooks/useSagemaker";
import { useVertexConnections } from "@/hooks/useVertex";
import { useAwsDiscoveryConnections } from "@/hooks/useAwsDiscovery";
import { useJiraIntegration } from "@/hooks/useJiraIntegration";
import { useSlackIntegration } from "@/hooks/useSlackIntegration";
import { INTEGRATIONS } from "@/types/integrations";

export function IntegrationsSection() {
  const { organization, isOrganizationOwner } = useOrganizationContext();
  const navigate = useNavigate();
  const [logfireDialogOpen, setLogfireDialogOpen] = useState(false);
  const [teamsDialogOpen, setTeamsDialogOpen] = useState(false);
  const [cloudwatchDialogOpen, setCloudwatchDialogOpen] = useState(false);
  const [jiraDialogOpen, setJiraDialogOpen] = useState(false);
  const [slackDialogOpen, setSlackDialogOpen] = useState(false);
  const [shadowAiDialogOpen, setShadowAiDialogOpen] = useState(false);
  const [vertexDialogOpen, setVertexDialogOpen] = useState(false);
  const { data: logfireTokensData } = useLogfireTokens();
  const { data: bedrockConnectionsData } = useBedrockConnections();
  const { data: sagemakerConnectionsData } = useSageMakerConnections();
  const { data: vertexConnectionsData } = useVertexConnections();
  const { data: jiraIntegration } = useJiraIntegration();
  const { data: slackIntegration } = useSlackIntegration();
  const { data: shadowAiConnections } = useAwsDiscoveryConnections();

  const logfireTokenCount = logfireTokensData?.data.length ?? 0;
  const cloudwatchConnectionCount =
    (bedrockConnectionsData?.data.length ?? 0) + (sagemakerConnectionsData?.data.length ?? 0);
  const jiraConfiguredCount = jiraIntegration ? 1 : 0;
  const slackConfiguredCount = slackIntegration ? 1 : 0;
  const shadowAiConnectionCount = shadowAiConnections?.length ?? 0;
  const vertexConnectionCount = vertexConnectionsData?.data.length ?? 0;
  const teamsConfigured = organization?.teams_webhook_url_last_four ? 1 : 0;

  return (
    <div className="space-y-6">
      <Card className="rounded-[15px] shadow-none">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Plug className="h-5 w-5" />
            <CardTitle>Integrations</CardTitle>
          </div>
          <CardDescription>
            Connect external services and platforms to enhance your security monitoring
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {INTEGRATIONS.map((integration) => {
              if (integration.featureFlag) {
                const configuredCount =
                  integration.id === "logfire"
                    ? logfireTokenCount
                    : integration.id === "aws_cloudwatch"
                      ? cloudwatchConnectionCount
                      : integration.id === "jira"
                        ? jiraConfiguredCount
                        : integration.id === "slack"
                          ? slackConfiguredCount
                          : integration.id === "aws_shadow_ai"
                            ? shadowAiConnectionCount
                            : integration.id === "gemini_agent_platform"
                              ? vertexConnectionCount
                              : 0;

                const requiresOwner = integration.id === "slack";
                const ownerDisabled = requiresOwner && !isOrganizationOwner;

                const onConfigure = ownerDisabled
                  ? undefined
                  : integration.id === "logfire"
                    ? () => setLogfireDialogOpen(true)
                    : integration.id === "aws_cloudwatch"
                      ? () => setCloudwatchDialogOpen(true)
                      : integration.id === "jira"
                        ? () => setJiraDialogOpen(true)
                        : integration.id === "slack"
                          ? () => setSlackDialogOpen(true)
                          : integration.id === "aws_shadow_ai"
                            ? () =>
                                shadowAiConnectionCount > 0
                                  ? navigate("/ai-inventory")
                                  : setShadowAiDialogOpen(true)
                            : integration.id === "gemini_agent_platform"
                              ? () => setVertexDialogOpen(true)
                              : undefined;

                return (
                  <FeatureGate
                    key={integration.id}
                    feature={integration.featureFlag as any}
                    fallback={
                      <IntegrationCard integration={{ ...integration, status: "disabled" }} />
                    }
                  >
                    <IntegrationCard
                      integration={integration}
                      configuredCount={configuredCount}
                      onConfigure={onConfigure}
                      disabled={ownerDisabled}
                      disabledReason="Only organization owners can configure this integration."
                    />
                  </FeatureGate>
                );
              }

              // Teams integration - grey out for non-owners
              if (integration.id === "teams") {
                return (
                  <FeatureGate
                    key={integration.id}
                    feature="alerts"
                    fallback={
                      <IntegrationCard
                        integration={{ ...integration, status: "disabled" }}
                      />
                    }
                  >
                    <IntegrationCard
                      integration={integration}
                      configuredCount={teamsConfigured}
                      onConfigure={isOrganizationOwner ? () => setTeamsDialogOpen(true) : undefined}
                      disabled={!isOrganizationOwner}
                      disabledReason="Only organization owners can configure this integration."
                    />
                  </FeatureGate>
                );
              }

              return <IntegrationCard key={integration.id} integration={integration} />;
            })}
          </div>
        </CardContent>
      </Card>

      <LogfireConfigDialog open={logfireDialogOpen} onOpenChange={setLogfireDialogOpen}>
        <div />
      </LogfireConfigDialog>

      {isOrganizationOwner && (
        <TeamsConfigDialog
          open={teamsDialogOpen}
          onOpenChange={setTeamsDialogOpen}
        >
          <div />
        </TeamsConfigDialog>
      )}

      <CloudWatchConfigDialog open={cloudwatchDialogOpen} onOpenChange={setCloudwatchDialogOpen}>
        <div />
      </CloudWatchConfigDialog>

      <JiraConfigDialog
        open={jiraDialogOpen}
        onOpenChange={setJiraDialogOpen}
      >
        <div />
      </JiraConfigDialog>

      {isOrganizationOwner && (
        <SlackConfigDialog
          open={slackDialogOpen}
          onOpenChange={setSlackDialogOpen}
        >
          <div />
        </SlackConfigDialog>
      )}

      <AwsDiscoveryConfigDialog
        open={shadowAiDialogOpen}
        onOpenChange={setShadowAiDialogOpen}
        onConnected={() => navigate("/ai-inventory")}
      >
        <div />
      </AwsDiscoveryConfigDialog>

      <VertexConfigDialog open={vertexDialogOpen} onOpenChange={setVertexDialogOpen}>
        <div />
      </VertexConfigDialog>
    </div>
  );
}
